// ============================================
// CONSTANTES DE CÓDIGOS HTTP
// ============================================

export const HTTP_STATUS = {
  // 2xx - Éxito
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,

  // 4xx - Errores del cliente
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  PAYLOAD_TOO_LARGE: 413,
  UNPROCESSABLE_ENTITY: 422,

  // 5xx - Errores del servidor
  SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
};

// ============================================
// FUNCIONES DE VALIDACIÓN
// ============================================

// ============================================
// SOPORTE TÉCNICO
// ============================================
export const SOPORTE_TECNICO = {
  telefono: "+54 9 3576 657682",
  whatsappUrl: "https://wa.me/5493576657682",
  textoDefault: "Hola Vaquiano! Tengo un error en el sistema:",
};

// ============================================
// FUNCIONES DE VALIDACIÓN Y DETECCIÓN
// ============================================

export const isSuccessStatus = (status) => {
  return status >= 200 && status < 300;
};

// Verifica error de autenticacion
export const isAuthError = (error) => {
  return error.response?.status === HTTP_STATUS.UNAUTHORIZED;
};

// Verifica error de permisos
export const isForbiddenError = (error) => {
  return error.response?.status === HTTP_STATUS.FORBIDDEN;
};

// Error de validacion
export const isValidationError = (error) => {
  const status = error.response?.status;
  return (
    status === HTTP_STATUS.BAD_REQUEST ||
    status === HTTP_STATUS.UNPROCESSABLE_ENTITY
  );
};

/**
 * Determina si el cliente está REALMENTE sin conexión a internet local
 */
export const isClientOffline = (error) => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return true;
  }
  if (error?.code === "ERR_INTERNET_DISCONNECTED") {
    return true;
  }
  return false;
};

/**
 * Determina si el error es del servidor, del sistema o de código interno nuestro
 */
export const isSystemOrServerError = (error) => {
  if (!error) return false;

  // Error JS runtime (TypeError, ReferenceError, etc.)
  if (
    !error.isAxiosError &&
    error.name !== "AxiosError" &&
    !error.config &&
    !error.response &&
    !error.request
  ) {
    return true;
  }

  // Código HTTP 5xx
  const status = error.response?.status;
  if (status && status >= 500) {
    return true;
  }

  // Error de red / sin respuesta cuando el usuario SÍ tiene internet (fallo del backend o infraestructura)
  if (!error.response && !isClientOffline(error)) {
    return true;
  }

  return false;
};

export const shouldContactSupport = (error) => {
  return isSystemOrServerError(error);
};

/**
 * Obtiene el título correspondiente al error según su naturaleza
 */
export const getErrorTitle = (error) => {
  if (isClientOffline(error)) {
    return "Sin Conexión";
  }

  const status = error?.response?.status;
  if (status && status >= 500) {
    return "Error del Servidor";
  }

  if (!error?.response && !isClientOffline(error)) {
    return "Error del Sistema";
  }

  if (
    error &&
    !error.isAxiosError &&
    error.name !== "AxiosError" &&
    !error.config &&
    !error.response
  ) {
    return "Error del Sistema";
  }

  if (
    status === HTTP_STATUS.BAD_REQUEST ||
    status === HTTP_STATUS.UNPROCESSABLE_ENTITY
  ) {
    return "Datos Inválidos";
  }

  if (status === HTTP_STATUS.UNAUTHORIZED || status === HTTP_STATUS.FORBIDDEN) {
    return "Acceso Denegado";
  }

  return "Error";
};

// ============================================
// MANEJO DE ERRORES
// ============================================

/**
 * Obtiene un mensaje de error amigable y transparente basado en el error HTTP o runtime.
 * NUNCA atribuye a "conexión a internet" fallos que sean del servidor o del código propio.
 * @param {Error} error - El objeto de error de Axios o Error de JavaScript
 * @param {string} defaultMessage - Mensaje por defecto si no se encuentra uno específico
 * @returns {string} - Mensaje de error formateado para el usuario
 */
export const getErrorMessage = (error, defaultMessage = "Error inesperado") => {
  // Identificar si es un error de código/sintaxis (JS runtime error)
  if (
    error &&
    !error.isAxiosError &&
    error.name !== "AxiosError" &&
    !error.config &&
    !error.response &&
    !error.request
  ) {
    const errorDetails = error.message ? ` (${error.message})` : "";
    return `Error interno de procesamiento${errorDetails}. Por favor, comuníquese con soporte técnico (${SOPORTE_TECNICO.telefono}).`;
  }

  // Error sin respuesta del servidor
  if (!error.response) {
    // Si el dispositivo está REALMENTE desconectado de internet
    if (isClientOffline(error)) {
      return "Sin conexión a internet. Verifique su red local o wifi e intente nuevamente.";
    }

    // El cliente tiene internet, pero el servidor falló o no respondió (problema nuestro)
    if (
      error.code === "ECONNABORTED" ||
      error.message?.toLowerCase().includes("timeout")
    ) {
      return `El servidor tardó demasiado en responder (tiempo de espera agotado). Por favor, comuníquese con soporte técnico (${SOPORTE_TECNICO.telefono}).`;
    }

    if (
      error.code === "ERR_NETWORK" ||
      error.message?.includes("Network Error")
    ) {
      return `Error de comunicación con el servidor. Se produjo un fallo en el sistema o el servidor no responde. Por favor, comuníquese con soporte técnico (${SOPORTE_TECNICO.telefono}).`;
    }

    if (error.request) {
      return `El servidor no respondió a la solicitud. Por favor, comuníquese con soporte técnico (${SOPORTE_TECNICO.telefono}).`;
    }

    const detailMsg = error.message ? ` (${error.message})` : "";
    return `Error interno al preparar la solicitud${detailMsg}. Por favor, comuníquese con soporte técnico (${SOPORTE_TECNICO.telefono}).`;
  }

  const { status, data } = error.response;

  // Prioridad 0: Errores del servidor (5xx) -> Son errores propios, no de internet
  if (status >= 500) {
    let serverDetail = "";
    if (typeof data?.detail === "string" && data.detail.trim()) {
      serverDetail = data.detail;
    } else if (typeof data?.message === "string" && data.message.trim()) {
      serverDetail = data.message;
    }

    if (serverDetail && serverDetail.toLowerCase().includes("soporte")) {
      return serverDetail;
    }

    if (serverDetail) {
      return `Error del servidor (${status}): ${serverDetail}. Por favor, comuníquese con soporte técnico (${SOPORTE_TECNICO.telefono}).`;
    }

    if (status === HTTP_STATUS.BAD_GATEWAY) {
      return `Error de comunicación con el servidor (Bad Gateway). Por favor, comuníquese con soporte técnico (${SOPORTE_TECNICO.telefono}).`;
    }
    if (status === HTTP_STATUS.SERVICE_UNAVAILABLE) {
      return `El servidor se encuentra temporalmente fuera de servicio. Por favor, reintente en unos minutos o comuníquese con soporte técnico (${SOPORTE_TECNICO.telefono}).`;
    }
    if (status === HTTP_STATUS.GATEWAY_TIMEOUT) {
      return `El servidor tardó demasiado en responder (Gateway Timeout). Por favor, comuníquese con soporte técnico (${SOPORTE_TECNICO.telefono}).`;
    }

    return `Error de procesamiento en el servidor (${status}). Por favor, comuníquese con soporte técnico (${SOPORTE_TECNICO.telefono}).`;
  }

  // Prioridad 1: Validación 422 (Pydantic / FastAPI)
  if (status === HTTP_STATUS.UNPROCESSABLE_ENTITY) {
    if (Array.isArray(data?.detail) && data.detail.length > 0) {
      const fieldErrors = data.detail.map((d) => {
        if (typeof d === "string") return d;
        const loc = Array.isArray(d.loc)
          ? d.loc.filter((l) => l !== "body").join(" -> ")
          : "";
        return loc
          ? `${loc}: ${d.msg || JSON.stringify(d)}`
          : d.msg || JSON.stringify(d);
      });
      return `Datos inválidos: ${fieldErrors.join(" | ")}`;
    }
    if (typeof data?.detail === "string" && data.detail.trim()) {
      return data.detail;
    }
    return "Los datos enviados no pudieron ser procesados. Verifique los campos ingresados.";
  }

  // Prioridad 2: Mensaje específico del backend (detail)
  if (data?.detail) {
    return typeof data.detail === "string"
      ? data.detail
      : JSON.stringify(data.detail);
  }

  // Prioridad 3: Mensaje genérico del backend (message)
  if (data?.message) {
    return data.message;
  }

  // Prioridad 4: Errores de validación por campo
  if (data?.errors && typeof data.errors === "object") {
    const errorFields = Object.keys(data.errors);
    if (errorFields.length > 0) {
      const fieldErrors = errorFields
        .map((field) => `${field}: ${data.errors[field]}`)
        .join(", ");
      return `Errores de validación - ${fieldErrors}`;
    }
  }

  // Mensajes específicos por código de estado
  const errorMessages = {
    [HTTP_STATUS.BAD_REQUEST]:
      "Datos inválidos. Verifique los campos ingresados.",
    [HTTP_STATUS.UNAUTHORIZED]:
      "Sesión expirada o credenciales incorrectas. Por favor, inicie sesión nuevamente.",
    [HTTP_STATUS.FORBIDDEN]: "No tiene permisos para realizar esta acción.",
    [HTTP_STATUS.NOT_FOUND]: "El recurso solicitado no fue encontrado.",
    [HTTP_STATUS.CONFLICT]:
      "El recurso ya existe o hay un conflicto con los datos.",
    [HTTP_STATUS.PAYLOAD_TOO_LARGE]:
      "El archivo es demasiado grande. Tamaño máximo: 10MB.",
  };

  if (errorMessages[status]) {
    return errorMessages[status];
  }

  return defaultMessage;
};

/**
 * Obtiene un mensaje de éxito basado en el código de estado
 * @param {number} status - Código de estado HTTP
 * @param {string} defaultMessage - Mensaje por defecto
 * @returns {string} - Mensaje de éxito
 *
 * @example
 * const message = getSuccessMessage(201, 'Operación completada');
 * // "Recurso creado exitosamente"
 */
export const getSuccessMessage = (
  status,
  defaultMessage = "Operación exitosa"
) => {
  const successMessages = {
    [HTTP_STATUS.OK]: "Operación completada exitosamente",
    [HTTP_STATUS.CREATED]: "Recurso creado exitosamente",
    [HTTP_STATUS.NO_CONTENT]: "Recurso completado sin contenido",
  };

  return successMessages[status] || defaultMessage;
};

/**
 * Registra el error en consola con formato consistente
 * @param {string} context - Contexto del error (ej: 'GET /api/usuarios')
 * @param {Error} error - El objeto de error
 *
 * @example
 * logError('POST /despachos', error);
 * // Console: ❌ [POST /despachos] Error: Datos inválidos
 */
export const logError = (context, error) => {
  const message = getErrorMessage(error);
  const status = error.response?.status || "Network Error";
  console.error(`❌ [${context}] ${status}: ${message}`);

  // En desarrollo, mostrar más detalles
  if (process.env.NODE_ENV === "development") {
    console.error("Error completo:", error);
  }
};

/**
 * Registra una operación exitosa en consola
 * @param {string} context - Contexto de la operación
 * @param {number} status - Código de estado HTTP
 *
 * @example
 * logSuccess('POST /despachos', 201);
 * // Console: ✅ [POST /despachos] 201: Recurso creado exitosamente
 */
export const logSuccess = (context, status) => {
  // const message = getSuccessMessage(status);
  // console.log(`✅ [${context}] ${status}: ${message}`);
};
